import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { galleryItems, galleryCategories } from '@/mocks/galleryData';

export default function GallerySection() {
  const { t, i18n } = useTranslation();
  const [filter, setFilter] = useState('all');
  const [lightbox, setLightbox] = useState<number | null>(null);
  const lang = i18n.language as 'fr' | 'en';

  const filtered = filter === 'all'
      ? galleryItems.filter((item) => item.category === 'all')
      : filter === 'courts'
        ? galleryItems.filter((item) => item.category === 'courts')
        : galleryItems.filter((item) => item.category === filter);

  const getAlt = (item: typeof galleryItems[0]) => (lang === 'en' ? item.altEn : item.alt);
  const getTitle = (item: typeof galleryItems[0]) => (lang === 'en' ? item.titleEn : item.title);

  return (
    <section id="gallery" className="py-16 md:py-24 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <span className="text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-accent-600">{t('gallery.title')}</span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mt-2">{t('gallery.subtitle')}</h2>
        </div>

        {/* Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {galleryCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setFilter(cat.id)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                filter === cat.id
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-50 text-foreground-600 hover:text-primary-600 hover:bg-primary-50 border border-foreground-200'
              }`}
            >
              {t(cat.key)}
            </button>
          ))}
        </div>

        {/* Grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-foreground-600">
            <i className="ri-image-line text-4xl mb-3 inline-block text-foreground-300"></i>
            <p className="text-sm font-medium">{t('gallery.empty', 'Sélectionnez une catégorie pour voir les photos')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {filtered.map((item) => (
              <div
                key={item.id}
                onClick={() => setLightbox(item.id)}
                className="group relative rounded-xl overflow-hidden cursor-pointer aspect-[4/3]"
              >
                <img
                  src={item.src}
                  alt={getAlt(item)}
                  className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-foreground-950/0 group-hover:bg-foreground-950/40 transition-all duration-300 flex items-end p-3">
                  <span className="text-background-50 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 translate-y-2 group-hover:translate-y-0">
                    {getTitle(item)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox !== null && (() => {
        const item = galleryItems.find((g) => g.id === lightbox);
        if (!item) return null;
        return (
          <div
            className="fixed inset-0 z-50 bg-foreground-950/90 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
          >
            <button
              onClick={() => setLightbox(null)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 text-background-50 hover:bg-background-50/30 transition-all cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                const idx = filtered.findIndex((g) => g.id === lightbox);
                if (idx > 0) setLightbox(filtered[idx - 1].id);
              }}
              className="absolute left-4 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 text-background-50 hover:bg-background-50/30 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                const idx = filtered.findIndex((g) => g.id === lightbox);
                if (idx < filtered.length - 1) setLightbox(filtered[idx + 1].id);
              }}
              className="absolute right-14 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 text-background-50 hover:bg-background-50/30 transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
            <img
              src={item.src}
              alt={getAlt(item)}
              className="max-w-full max-h-[85vh] rounded-xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="absolute bottom-6 text-background-50 text-sm font-medium">
              {getTitle(item)}
            </p>
          </div>
        );
      })()}
    </section>
  );
}